import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, CircularProgress, Box, Grid } from "@mui/material";
import type { Subscription, ServerLicenseDto, CloudSubscriptionDto } from "client/api-v2";
import type { RoleDto } from "client/api/RolesApi";
import { cloudSubscriptionUserGrantsApi, serverLicenseUserGrantsApi } from "client/api/UserGrantApi";
import type { UserDto } from "client/api/UsersApi";
import { useEntity } from "components/EntityProvider/EntityProvider";
import { RoleSelector } from "components/RoleSelector/RoleSelector";
import { UserSelector } from "components/UserSelector/UserSelector";
import { SubmittableFormLayout } from "components/form/SubmittableFormLayout";
import {
  getServerLicenseBySubscriptionId,
  getCloudSubscriptionBySubscriptionId,
} from "../services/subscriptionService";

type Inputs = {
  user: UserDto;
  role: RoleDto;
};

/**
 * Add User Access form for Subscriptions.
 * This component determines the platform type (Server or Cloud) and creates
 * a user grant on the underlying platform entity.
 */
export function SubscriptionAddUserAccess() {
  const { entity: subscription } = useEntity<Subscription>();
  const navigate = useNavigate();
  const [platformEntityId, setPlatformEntityId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isServerPlatform = subscription?.plan?.productPlatform === "OctopusServer";
  const isCloudPlatform = subscription?.plan?.productPlatform === "OctopusCloud";

  useEffect(() => {
    if (!subscription?.id) {
      setLoading(false);
      setError("Subscription not found");
      return;
    }

    setLoading(true);
    setError(null);

    if (isServerPlatform) {
      getServerLicenseBySubscriptionId(subscription.id)
        .then((license: ServerLicenseDto) => {
          setPlatformEntityId(license.id);
        })
        .catch((err) => {
          console.error("Failed to fetch server license:", err);
          setError("Failed to load server license information");
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (isCloudPlatform) {
      getCloudSubscriptionBySubscriptionId(subscription.id)
        .then((cloudSub: CloudSubscriptionDto) => {
          setPlatformEntityId(cloudSub.id);
        })
        .catch((err) => {
          console.error("Failed to fetch cloud subscription:", err);
          setError("Failed to load cloud subscription information");
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
      setError("Unsupported subscription platform type");
    }
  }, [subscription?.id, isServerPlatform, isCloudPlatform]);

  const onSubmit = async (params: Inputs) => {
    if (!platformEntityId) {
      throw new Error("Platform entity ID not available");
    }

    const userGrantsApi = isServerPlatform ? serverLicenseUserGrantsApi : cloudSubscriptionUserGrantsApi;
    await userGrantsApi.createUserGrant(platformEntityId, params.user.id, params.role.id);

    // Navigate back to the user access page
    navigate(-1);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !platformEntityId) {
    return <Alert severity="error">{error || "Unable to load subscription information"}</Alert>;
  }

  const roleType = isServerPlatform ? "ServerLicense" : "CloudSubscription";

  return (
    <SubmittableFormLayout header="Create new assignment" submitAction={onSubmit}>
      {(formController) => {
        const { control } = formController;
        return (
          <Grid container spacing={3}>
            <Grid size={6}>
              <UserSelector
                label="User"
                name="user"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: "Please select a user",
                  },
                }}
              />
            </Grid>
            <Grid size={6}>
              <RoleSelector
                label="Role"
                roleType={roleType}
                name="role"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: "Please select a role",
                  },
                }}
              />
            </Grid>
          </Grid>
        );
      }}
    </SubmittableFormLayout>
  );
}
